/**
 * SaveButton — reusable bookmark control for ANY research object.
 *
 * Works with all 14 bookmark kinds (topic, source, event, entity, vaccine,
 * vaccine_schedule, vaccine_age_cohort, vaccine_comparison, evidence_block,
 * research_area, ingredient, disease, manufacturer, surveillance_system).
 *
 * Future explorers (Medicines, Chemicals, Food Additives) drop this same
 * component next to their own savable objects with zero changes.
 *
 * Props
 *   kind        Bookmark kind — one of ALLOWED_BOOKMARK_KINDS.
 *   refSlug     Canonical identifier (slug, UUID, or "slug#fragment").
 *   refTitle    Human-readable title shown on the My Research page.
 *   parentRef   Optional research-trail parent (e.g. "vaccine:mmr") — this
 *               is the magic that lets Dashboard group saves by trail.
 *   fragment    Optional URL fragment for jumping to a section on open.
 *   refUrl      Optional explicit canonical URL (for comparison pages etc).
 *   variant     "icon" | "pill" — visual treatment.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Bookmark, BookmarkCheck, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const STORE_KEY = "ctx_bookmarks_v1";

function readCache() {
    try { return JSON.parse(localStorage.getItem(STORE_KEY) || "{}"); }
    catch { return {}; }
}
function writeCache(next) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
}
function cacheKey(kind, refSlug) { return `${kind}::${refSlug}`; }

export default function SaveButton({
    kind,
    refSlug,
    refTitle,
    parentRef = null,
    fragment = null,
    refUrl = null,
    variant = "icon",
    className = "",
    testId,
}) {
    const { user } = useAuth();
    const [saved, setSaved] = useState(false);
    const [bookmarkId, setBookmarkId] = useState(null);
    const [busy, setBusy] = useState(false);

    // Hydrate from local cache for instant paint; revalidate from server.
    useEffect(() => {
        const cache = readCache();
        const hit = cache[cacheKey(kind, refSlug)];
        if (hit) { setSaved(true); setBookmarkId(hit.id); }
    }, [kind, refSlug]);

    useEffect(() => {
        if (!user) { setSaved(false); setBookmarkId(null); return; }
        let cancelled = false;
        api.get("/me/bookmarks").then(({ data }) => {
            if (cancelled) return;
            const match = (data || []).find((b) => b.kind === kind && b.ref_slug === refSlug);
            if (match) {
                setSaved(true); setBookmarkId(match.id);
                const cache = readCache();
                cache[cacheKey(kind, refSlug)] = { id: match.id };
                writeCache(cache);
            } else {
                setSaved(false); setBookmarkId(null);
                const cache = readCache();
                delete cache[cacheKey(kind, refSlug)];
                writeCache(cache);
            }
        }).catch(() => { /* silent — anonymous */ });
        return () => { cancelled = true; };
    }, [user, kind, refSlug]);

    const toggle = useCallback(async (e) => {
        e.preventDefault(); e.stopPropagation();
        if (!user) {
            toast.message("Sign in to save", { description: "Members can build research trails across the archive." });
            return;
        }
        if (busy) return;
        setBusy(true);
        try {
            if (saved && bookmarkId) {
                await api.delete(`/me/bookmarks/${bookmarkId}`);
                setSaved(false); setBookmarkId(null);
                const cache = readCache();
                delete cache[cacheKey(kind, refSlug)];
                writeCache(cache);
                toast.success("Removed from My Research");
            } else {
                const payload = { kind, ref_slug: refSlug, ref_title: refTitle };
                if (parentRef) payload.parent_ref = parentRef;
                if (fragment) payload.fragment = fragment;
                if (refUrl) payload.ref_url = refUrl;
                const { data } = await api.post("/me/bookmarks", payload);
                setSaved(true); setBookmarkId(data.id);
                const cache = readCache();
                cache[cacheKey(kind, refSlug)] = { id: data.id };
                writeCache(cache);
                toast.success("Saved to My Research", {
                    description: parentRef ? `Added to your '${parentRef.split(":")[1] || "research"}' trail` : undefined,
                });
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't update bookmark.");
        } finally {
            setBusy(false);
        }
    }, [user, saved, bookmarkId, busy, kind, refSlug, refTitle, parentRef, fragment, refUrl]);

    if (variant === "pill") {
        return (
            <button
                type="button"
                onClick={toggle}
                disabled={busy}
                data-testid={testId || `save-${kind}-${refSlug}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium tracking-wide transition-colors disabled:opacity-50 ${
                    saved
                        ? "bg-[#56CCF2]/15 text-[#56CCF2] border border-[#56CCF2]/40 hover:bg-[#56CCF2]/25"
                        : "bg-[#111827] text-[#94A3B8] border border-[#243044] hover:border-[#56CCF2]/50 hover:text-white"
                } ${className}`}
            >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : saved ? <BookmarkCheck className="w-3 h-3" /> : <Bookmark className="w-3 h-3" />}
                {saved ? "Saved" : "Save"}
            </button>
        );
    }

    return (
        <button
            type="button"
            onClick={toggle}
            disabled={busy}
            aria-label={saved ? "Remove from My Research" : "Save to My Research"}
            data-testid={testId || `save-${kind}-${refSlug}`}
            className={`inline-flex items-center justify-center w-7 h-7 rounded-md transition-colors disabled:opacity-50 ${
                saved ? "text-[#56CCF2] hover:text-white" : "text-[#64748B] hover:text-[#56CCF2]"
            } ${className}`}
        >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
        </button>
    );
}
